import { useEffect } from 'react';

/**
 * Dynamically sets per-page SEO meta tags.
 *
 * Updates document.title, <meta name="description">, <link rel="canonical">,
 * and Open Graph / Twitter Card tags whenever the values change.
 */
export interface PageMeta {
  title: string;
  description: string;
  canonical: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
}

const SITE_NAME = 'Noodle Studio';
const SITE_URL = 'https://noodlestudio.ai';
const OG_IMAGE = `${SITE_URL}/og-image.png`;

function setMetaTag(name: string, content: string, attribute: 'name' | 'property' = 'name') {
  let el = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attribute, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLinkTag(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function usePageMeta({ title, description, canonical, ogTitle, ogDescription, ogType }: PageMeta) {
  useEffect(() => {
    // Title
    document.title = title;

    // Standard meta
    setMetaTag('description', description);

    // Canonical
    setLinkTag('canonical', canonical);

    // Open Graph
    setMetaTag('og:title', ogTitle || title, 'property');
    setMetaTag('og:description', ogDescription || description, 'property');
    setMetaTag('og:url', canonical, 'property');
    setMetaTag('og:type', ogType || 'website', 'property');
    setMetaTag('og:image', OG_IMAGE, 'property');
    setMetaTag('og:site_name', SITE_NAME, 'property');

    // Twitter Card
    setMetaTag('twitter:title', ogTitle || title, 'name');
    setMetaTag('twitter:description', ogDescription || description, 'name');
    setMetaTag('twitter:url', canonical, 'name');
    setMetaTag('twitter:image', OG_IMAGE, 'name');
  }, [title, description, canonical, ogTitle, ogDescription, ogType]);
}

/** Pre-defined per-page meta for the static/public pages */
export const PAGE_META = {
  home: {
    title: 'Noodle Studio — Tools for people who care how it ends',
    description: 'A studio for writers, directors and producers: ideas, script, cast, boards, shots and the cut, side by side. Bring your own API keys for the models; nothing added on top. In beta, free to start.',
    canonical: `${SITE_URL}/`,
  },
  about: {
    title: 'About Noodle Studio — AI Creative Workspace',
    description: 'Learn about Noodle Studio, the AI-powered infinite canvas for image generation, video creation, storyboarding, and creative content.',
    canonical: `${SITE_URL}/about`,
  },
  terms: {
    title: 'Terms of Service — Noodle Studio',
    description: 'Read the Terms of Service for Noodle Studio, the AI-powered creative workspace.',
    canonical: `${SITE_URL}/terms`,
  },
  privacy: {
    title: 'Privacy Policy — Noodle Studio',
    description: 'Read the Privacy Policy for Noodle Studio. Learn how Noodle Studio collects, uses, and protects your personal information.',
    canonical: `${SITE_URL}/privacy`,
  },
  subscription: {
    title: 'Pricing & Subscription — Noodle Studio',
    description: 'Explore Noodle Studio pricing plans. Get more AI generations, cloud storage, and premium features. Pay with USDC on Solana.',
    canonical: `${SITE_URL}/subscription`,
  },
  settings: {
    title: 'Settings — Noodle Studio',
    description: 'Manage your Noodle Studio account settings, API keys, connected devices, and preferences.',
    canonical: `${SITE_URL}/settings`,
  },
  analytics: {
    title: 'Usage Analytics — Noodle Studio',
    description: 'View your Noodle Studio usage analytics, generation counts, and workspace statistics.',
    canonical: `${SITE_URL}/analytics`,
  },
  workspace: {
    title: 'Your Workspaces — Noodle Studio',
    description: 'Browse, create, and manage your AI creative workspaces in Noodle Studio.',
    canonical: `${SITE_URL}/workspaces`,
  },
  notFound: {
    title: '404 — Page Not Found | Noodle Studio',
    description: 'This page doesn\u2019t exist on Noodle Studio. Head back to the canvas and keep creating.',
    canonical: `${SITE_URL}/`,
  },
} as const;
